import React from "react";
import { Alert, Collapse, List, Typography } from "antd";
import exampleWave from "../../../functions/__fixtures__/wave.example.json";

const { Title, Paragraph, Text } = Typography;

const Steps = ({ items }) => (
  <List
    size="small"
    dataSource={items}
    renderItem={(item, index) => (
      <List.Item>
        <Text strong style={{ marginRight: 8 }}>
          {index + 1}.
        </Text>
        {item}
      </List.Item>
    )}
  />
);

/**
 * In-app documentation for the order picking system: how the warehouse works with it, and the
 * contract the scanner app is built against. The example is the tested fixture itself
 * (functions/__fixtures__/wave.example.json), so it cannot drift from what the CMS produces.
 */
const OrderpickingDocumentation = () => (
  <div>
    <Title level={2}>Orderpicken</Title>
    <Paragraph>
      Het CMS rekent elke pickronde volledig uit: welke artikelen, in welk vak, in welke volgorde en in welke doos. De
      scanner in de vriezer volgt die ronde en weet zelf niets van producten of pakketten. Na het picken verwerkt het
      CMS wat er gepickt is op de bestellingen van beide webshops.
    </Paragraph>

    <Title level={3}>Eenmalig: het magazijn inrichten</Title>
    <Steps
      items={[
        <span>
          <Text strong>Orderpicking → Vakken en looproute.</Text> Elke regel is een vak. Elke rij stellingen heeft een
          eigen nummer, en de stellingen in een rij tellen in looprichting vanaf 01: 203C is rij 2, rek 3, mand C. Een
          rij groeit aan het eind (een negende pallet is 109) zonder dat er iets anders verandert. De letters gaan per
          mand of kistenstapel, van boven naar beneden en van links naar rechts. De zone (Manden of Vloer) zegt waar je
          moet kijken; de scanner toont hem onder de vakcode.
        </span>,
        <span>
          <Text strong>Vakken vullen</Text>: loop de vriezer langs en kies per vak welk artikel erin ligt. Geef het
          eerste vak en het aantal vakken per stelling op (bijvoorbeeld 8, 8, 6), typ per vak een paar letters, kies met
          de pijltjes en druk op Enter. Het gekoppelde product van de andere shop gaat mee, en een vak dat nog niet
          bestaat wordt gemaakt. Enter zonder zoekterm laat een vak zoals het is. De bordjetekst gaat mee met wat erin
          komt.
        </span>,
        <span>
          <Text strong>Kaart</Text> (tabblad naast Tabel): de vriezer van bovenaf, met per stelling hoeveel vakken gevuld
          zijn (groen is alles gevuld, oranje een bordje dat niet klopt, rood meer dan één artikel in een vak). Klik op een
          stelling voor de manden zoals je ervoor staat, en op een mand om dat vak in de tabel te openen. Rekken en manden
          komen uit de vakcodes; alleen waar de rijen staan ligt vast.
        </span>,
        <span>
          <Text strong>Bordje klopt niet</Text>: noemt de bordjetekst van een vak iets anders dan wat erin ligt
          (&quot;Kogelbiefstuk 150 gram&quot; op het vak met de 125 gram), dan staat er bij dat vak &quot;Ligt hier: …&quot;
          en bovenaan een melding. Het vinkje <Text strong>Bordje klopt niet</Text> toont alleen die vakken. Ligt het
          product goed, klik dan op <Text strong>Gelijkzetten</Text> (of <Text strong>Allemaal gelijkzetten</Text> in de
          melding); ligt het fout, zet het dan met Vakken vullen in het goede vak. De scanner toont altijd wat de
          bestelling vraagt; het bordje en het productkaartje tonen de bordjetekst.
        </span>,
        <span>
          <Text strong>Meer → Looproute op vakcode zetten</Text>: nieuwe vakken staan achteraan; dit zet de vakken in
          codevolgorde vooraan, rij voor rij. Controleer de volgorde en klik op <Text strong>Volgorde opslaan</Text>.
        </span>,
        <span>
          <Text strong>Meer → Vakken per gewicht maken.</Text> Elk gewicht van een product krijgt een eigen vak en
          barcode. VCE- en HVO-producten met hetzelfde gewicht komen in hetzelfde vak.
        </span>,
        <span>
          Geef elk vak een <Text strong>vakcode</Text> (1A, 1B, …): per vak via het potlood (Enter gaat door naar het
          volgende vak zonder code), of in één keer via <Text strong>Meer → Vakken doornummeren</Text>, met per stelling
          het aantal vakken: 8 voor een rek van 4 hoog en 2 breed, 6 voor een rek van 3 hoog (bijvoorbeeld 8, 8, 6).
        </span>,
        <span>
          <Text strong>Vaketiketten printen</Text>: kaartjes van 88 × 46 mm voor de kaarthouders op de manden, twee
          naast elkaar in één houder (12 per A4, op stevig papier, knippen langs de stippellijn). Links het{" "}
          <Text strong>vakkaartje</Text> met vakcode en barcode, rechts het <Text strong>productkaartje</Text> met wat er
          ligt en het gewicht groot, zonder barcode. Een leeg vak krijgt geen productkaartje. Verhuist er een product,
          print dan alleen een nieuw productkaartje: selecteer het vak en kies Productkaartjes. Voor de magneethouders op
          de balken boven de pallets (rij 1) zijn er dezelfde kaartjes klein: 98 × 40 mm, één per houder, 14 per A4. Ze klemmen
          tussen de randjes van de houder, dus snijd ze precies op maat. Of
          etiketvellen met 8 etiketten (99,1 × 67,7 mm). Printer op 100%. Scan er een paar ter controle.
        </span>,
        <span>
          <Text strong>Meer → Stellingbordjes printen</Text>: één A4 per stelling met het stellingnummer groot, voor aan
          de kop van de stelling. Een picker zoekt zo eerst de stelling en dan de letter. Lamineren voor de vriezer.
        </span>,
        <span>
          <Text strong>Orderpicking → Productgegevens</Text>: vul pickgewicht en volumegewicht in, vooral voor producten
          zonder gewichtsvariant. Daarop rust het doosadvies. Sorteer op <Text strong>Vak</Text> om de looproute te volgen:
          de hardlopers staan dan bovenaan. De artikelcodes staan daar ook, en zijn te exporteren. Vul ook{" "}
          <Text strong>stuks per verpakking</Text> in voor wat per stuk verkocht en per meer verpakt wordt (runderburgers
          per 4, kipschnitzels per 2): de scanner telt dan per scan een hele verpakking. Bij een product met gewichten is
          dat per gewicht: kogelbiefstuk van 125 en 150 gram per 2, de zwaardere per stuk. Het filter{" "}
          <Text strong>Verpakking nalopen</Text> toont wat de webshop in stappen van meer dan één verkoopt. Het pickgewicht
          blijft het gewicht van één stuk, dus van één burger.
        </span>,
        <span>
          <Text strong>Orderpicking → Pickers → Pasjes printen</Text>: één naam per regel. Elk pasje krijgt een eigen code
          (BADGE-0001, …) en komt met de naam in het pasjesregister. Knippen en lamineren; de picker scant het aan het
          begin van een ronde.
        </span>,
      ]}
    />

    <Title level={3}>Elke pickdag</Title>
    <Paragraph>
      Bovenaan Bestellingen, Pickrondes en Tekorten staat de <Text strong>stappenlijst</Text> van de pickdag: hoe ver
      elke stap is en wat de volgende stap is, met een knop die naar het juiste scherm gaat. Dit zijn de stappen:
    </Paragraph>
    <Steps
      items={[
        <span>
          <Text strong>Bestellingen</Text>: kies de dag en klik op <Text strong>Pickdag vrijgeven</Text>. De bestellingen
          van die dag liggen dan vast; aanpassen kan pas weer na ontgrendelen.
        </span>,
        <span>
          <Text strong>Pickronde samenstellen</Text>: standaard de 20 oudste bestellingen, of de bestellingen die je in de
          tabel hebt aangevinkt. Bestellingen die niet uit te rekenen zijn (een verwijderd product, een kapot pakket)
          kunnen niet mee en staan er met de reden bij.
        </span>,
        <span>
          <Text strong>Pickrondes</Text>: print de <Text strong>pakbonnen</Text> van de ronde. Bovenop ligt een voorblad
          met de dozen die mee de vriezer in gaan (zoveel kleine, zoveel grote) en de bestellingen in stapelvolgorde.
          Daaronder de pakbonnen in pickvolgorde, met een barcode om de bestelling op de scanner te starten. De pakbon
          gaat in de doos van de klant en is daarom kaal: geen vak, doosmaat, rondenummer of pickinstructie, die toont de
          scanner. Werkt de scanner niet, kies dan bij het printen (pijltje naast de printer){" "}
          <Text strong>Pakbonnen om zonder scanner te picken</Text>: die hebben per regel het vak en de pickinstructie,
          de doosmaat en "Ronde 3 · bestelling 4 van 20". De scanner noemt de dozen ook, bij de ronde en voor de eerste
          bestelling.
        </span>,
        <span>
          Het <Text strong>commandokaartje</Text> (Pickrondes → Commandokaart printen) hangt gelamineerd aan het karretje:
          vak leeg, doos vol, andere doos, regel zonder vak bevestigen (één scan per stuk of verpakking, zoals bij een
          vak), order afbreken, pauze.
        </span>,
        <span>
          Na het picken verwerkt de Pickrondes-pagina de resultaten vanzelf zodra de scanner weer verbinding heeft. In het
          bestellingenoverzicht staat per bestelling de status in de kolom <Text strong>Picking</Text>; bij de bestelling
          zelf staan picker, tijden, dozen en uitzonderingen.
        </span>,
        <span>
          <Text strong>Orderpicking → Tekorten</Text>: de bestellingen die de vriezer niet compleet verlieten (een vak was
          leeg, of de bestelling is afgebroken), met wat er ontbreekt en het telefoonnummer van de klant. Leg na het bellen
          vast wat is afgesproken: later leveren, geld terug, een vervangend product, of niet bereikbaar.
        </span>,
      ]}
    />

    <Title level={3}>Pickers en hun cijfers</Title>
    <Paragraph>
      <Text strong>Orderpicking → Pickers</Text> toont per pasje wat er in een periode gepickt is: rondes, orders, regels,
      stuks, picktijd, de gemiddelde tijd per order en stuks per uur. Het tabblad Rondes toont elke ronde met zijn orders. De cijfers
      komen rechtstreeks uit de scans, dus ze staan er zodra de scanner verbinding heeft gehad.
    </Paragraph>
    <Paragraph>
      <Text strong>Picktijd</Text> is de tijd tussen twee scans. Een pauze (PAUZE op de commandokaart) telt niet mee, en
      een gat van meer dan 10 minuten zonder scan ook niet: dat is een pauze zonder de pauzeknop. De{" "}
      <Text strong>tijd per order</Text> loopt van het scannen van de pakbon tot de order klaar is; het wisselen naar de
      volgende order (doos weg, volgende pakbon) telt voor de ronde.
    </Paragraph>
    <Paragraph>
      Een pasje hoort bij één persoon. Een verloren pasje print je opnieuw met alleen zijn code; een nieuwe picker krijgt
      een nieuw pasje. Wie niet meer pickt, zet je uit: de historie blijft staan. Met{" "}
      <Text strong>Exporteren</Text> gaan de cijfers per picker, per ronde en per order naar Excel.
    </Paragraph>

    <Alert
      type="info"
      showIcon
      className="mb-3"
      message="Ontgrendelen en annuleren"
      description="Een ronde die nog niemand gestart heeft kun je annuleren; de bestellingen komen dan weer vrij. Een pickdag ontgrendel je pas als er geen ronde meer open staat of gepickt wordt."
    />

    <Collapse>
      <Collapse.Panel header="Voor ontwikkelaars: het wave-contract (v1)" key="contract">
        <Paragraph>
          Volledige beschrijving: <Text code>fulfilment-cms/docs/wave-contract-v1.md</Text>. De ronde staat in{" "}
          <Text code>pickWaves/{"{waveId}"}</Text> in het VCE-project; de app schrijft events naar{" "}
          <Text code>pickWaves/{"{waveId}"}/events/{"{eventId}"}</Text>. Codes worden alleen exact en binnen de eigen ronde
          vergeleken.
        </Paragraph>
        <pre style={{ background: "#f5f5f5", padding: 16, borderRadius: 4, overflowX: "auto", maxHeight: 480 }}>
          {JSON.stringify(exampleWave, null, 2)}
        </pre>
      </Collapse.Panel>
    </Collapse>
  </div>
);

export default OrderpickingDocumentation;
